import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import {
  Inject,
  Injectable,
  Injector,
  PLATFORM_ID,
  TransferState
} from '@angular/core';
import { Validators } from '@angular/forms';
import { OauthAEMConfig } from '@com-delta-idp/mnx-oauth';
import { LoggingService, namePatterns } from '@com-delta-idp/mnx-utils';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';
import { CookieHelper } from '../../common/cookie-helper';
import { ApplicationConfig } from '../../config/config';
import { UrlUtil } from '../../config/urlutil';
import { HTTP_TIMEOUT } from '../../interceptor/timeout.interceptor';
import { ContentHeaderFooter } from '../../model/content/contentheaderfooter.model';

// const HEADERFOOTERDATA = makeStateKey('headerData');
@Injectable({
  providedIn: 'root'
})
export class MemoryLeakDetector {
  headerFooterRegistry = new Map<string, ContentHeaderFooter>();
  announcer = new Subject<Error>();

  constructor() {}

  get length() {
    return this.headerFooterRegistry.size;
  }

  register(content: ContentHeaderFooter, id?: string) {
    const _id = id ? id : `${Math.floor(Math.random())}_${Date.now()}`;
    if (this.headerFooterRegistry.get(_id)) {
      return this.headerFooterRegistry.get(_id);
    }
    return this.headerFooterRegistry.set(id, content).get(id);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ContentHeaderFooterService {
  private reqURL = '';
  private reqPort = '';
  private _contentObservable: Observable<any>;
  private countryCodeFromReq = '';
  private countryCodes: void | null = null;
  public _isHeaderFooter = new BehaviorSubject(false);
  constructor(
    private injector: Injector,
    private _http: HttpClient,
    private config: ApplicationConfig,
    private urlUtil: UrlUtil,
    private cookieHelper: CookieHelper,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cache: TransferState,
    private loggingSerivce: LoggingService
  ) {
    this.countryCodes = this.getCountryCode();
  }

  private _contentHeaderFooter: ContentHeaderFooter;

  get contentHeaderFooter(): Observable<any> {
    if (this.config.onBrowser && !this.countryCodeFromReq) {
      this.getCountryCode();
    }
    /*let headerFooterFromMemoryLeak = this.leak.headerFooterRegistry.get(this.countryCodeFromReq);
    if (headerFooterFromMemoryLeak && this.config.get('APP_ENABLE_SSR') === 'true') {
      // register the contentHeaderFooter in the Map<>
      this._contentHeaderFooter = headerFooterFromMemoryLeak;
    }*/
    const getheaderfooterArr = [
      {
        condition: () => this._contentHeaderFooter,
        action: () => of(this._contentHeaderFooter)
      },
      {
        condition: () => !this._contentHeaderFooter && this._contentObservable,
        action: () => this._contentObservable
      },
      {
        condition: () =>
          !this._contentHeaderFooter &&
          !this._contentObservable &&
          isPlatformBrowser(this.platformId),
        action: () => (this._contentObservable = this.getHeaderFooterFromAPI())
      }
    ];

    let headerFooter = getheaderfooterArr.filter((obj) => {
      if (obj.condition()) {
        return obj.action();
      }
    });

    if (headerFooter.length) {
      return headerFooter[0].action();
    }
  }

  /**
   * Connects to the Content URL and caches the current observable request.
   * A map projection caches the data result and uses that for future requests.
   * Can be refreshed on full page reload.
   */
  getHeaderFooterFromAPI() {
    let result = this.contentHeaderFooterAPI(this.countryCodeFromReq).pipe(
      map((data) => {
        // when the data projects, we don't need the Observable anymore
        this._contentObservable = null;
        this._contentHeaderFooter = new ContentHeaderFooter(data);
        // this.cache.set(HEADERFOOTERDATA, data);
        this._isHeaderFooter.next(true);
        return this._contentHeaderFooter;
      }),
      share()
    );
    return result;
  }

  getcountryCodeFromReq() {
    if (this.injector.get('REQUEST_TOKEN')) {
      const requestObject = this.injector.get('REQUEST_TOKEN');
      if (requestObject.query['loc']) {
        this.countryCodeFromReq = requestObject.query['loc'];
      }
    }
  }

  public contentHeaderFooterForSEO(countryCode): Observable<any> {
    let headerFooterRespForSEO;
    headerFooterRespForSEO = this.getHeaderFooterResponse(countryCode);
    return headerFooterRespForSEO;
  }

  // TODO move this to a common service util
  private handleError(error: HttpErrorResponse) {
    if (
      isPlatformBrowser(this.platformId) &&
      error.error instanceof ErrorEvent
    ) {
      // A client-side or network error occurred. Handle it accordingly.
      this.loggingSerivce.log(
        'An error occurred on loading headerfooter JSON:' + error.error.message
      );
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code on loading headerfooter JSON ${error.status}, ` +
          `body was: ${error.error}`
      );
    }
    // return an ErrorObservable with a user-facing error message
    return throwError(
      'Something bad happened on loading headerfooter JSON; please try again later.'
    );
  }

  private getDefaultCountry() {
    return this.config.get('APP_DEFAULT_COUNTRY')
      ? this.config.get('APP_DEFAULT_COUNTRY')
      : 'us';
  }

  private getCountryCode() {
    if (isPlatformBrowser(this.platformId)) {
      this.countryCodeFromReq = this.cookieHelper.getCountryCode();
    } else if (isPlatformServer(this.platformId)) {
      this.getcountryCodeFromReq();
    }

    if (!this.countryCodeFromReq) {
      this.countryCodeFromReq = this.getDefaultCountry();
    }
  }

  private contentHeaderFooterAPI(countryCodeFromReq): Observable<any> {
    let headerfooterresp;
    const svcTimeOut: number = Number.parseInt(
      this.config.get('APP_TIMEOUT_FAST')
    );
    let countryCode = '';
    if (this.config.onServer && countryCodeFromReq) {
      countryCode = countryCodeFromReq;
    } else {
      countryCode = this.cookieHelper.getCountryCode();
    }
    if (!countryCode) {
      countryCode = this.config.get('APP_DEFAULT_COUNTRY');
    }

    // setting requested country code to the country for which value is retrieved
    this.countryCodeFromReq = countryCode;
    headerfooterresp = this.getHeaderFooterResponse(countryCode);
    return headerfooterresp;
  }

  private getHeaderFooterResponse(countryCode) {
    const countryCheck = [
      {
        condition: () => !countryCode && this.config.get('APP_DEFAULT_COUNTRY'),
        action: () => {
          console.error(
            'country code is empty from pref cookie and request token, taking it from APP_DEFAULT_COUNTRY'
          );
          countryCode = this.config.get('APP_DEFAULT_COUNTRY');
        }
      },
      {
        condition: () =>
          !countryCode && !this.config.get('APP_DEFAULT_COUNTRY'),
        action: () => {
          console.error(
            'country code is empty from pref cookie and request token, taking it from APP_DEFAULT_COUNTRY'
          );
          console.error(
            'Falling to fail safe as APP_DEFAULT_COUNTRY is empty , setting country as US'
          );
          countryCode = 'us';
        }
      }
    ];

    countryCheck.filter((obj) => {
      if (obj.condition()) {
        obj.action();
      }
    });
    let headerfooterresp;
    let svcTimeOut: number;
    if (this.config.get('APP_TIMEOUT_HEADER_FOOTER')) {
      svcTimeOut = Number.parseInt(
        this.config.get('APP_TIMEOUT_HEADER_FOOTER')
      );
    } else {
      svcTimeOut = 15000;
    }
    let app_content_config_url = this.config.get('APP_CONTENT_CONFIG_URL');
    if (app_content_config_url.search('{param1}') > 0) {
      if (isPlatformBrowser(this.platformId)) {
        app_content_config_url = app_content_config_url.replace(
          '{param1}',
          this.urlUtil.getCountryCodeForUrl(countryCode)
        );
      } else {
        app_content_config_url = app_content_config_url.replace(
          '{param1}',
          countryCode
        );
      }
    }
    // build the client request, piping to the retry and error handlers
    // and also automatically convert to the model object
    let headers = new HttpHeaders();
    headers = headers.set(HTTP_TIMEOUT, svcTimeOut.toString());
    headers = headers.set('Content-Type', 'application/json; charset=UTF-8');
    const requestOptions = { headers: headers, params: new HttpParams() };
    headerfooterresp = this._http
      .get(`${app_content_config_url}`, requestOptions)
      .pipe(catchError(this.handleError));
    return headerfooterresp;
  }

  getValidator(authConfig: OauthAEMConfig, key: string) {
    if (authConfig?.NAME_LENGTH_CONFIG) {
      return [Validators.required, Validators.pattern(namePatterns[key])];
    } else {
      return [
        Validators.required,
        Validators.maxLength(50),
        Validators.pattern("^[a-zA-Z' ]+$")
      ];
    }
  }
}
